import { useVerifyLandlordIdentityExtendedWebApiHandler_verifyLandlordIdentityExtended } from '@zg-rentals/ts-rent-payment-client-react-query';
import { logError } from '@zg-rentals/log-error';

type Options = {
  postVerificationFlow?: () => void | Promise<void>;
  /** Called when the API returns verifyErrors (identity check failed, not a network error). */
  onVerifyError?: (errors: Record<string, unknown>) => void | Promise<void>;
  /** Called for network/mutation errors; falls back from onVerifyError when omitted. */
  onError?: (error: unknown) => void | Promise<void>;
  /** Identity passed but a later step failed; falls back to onError when omitted. */
  onPostVerificationError?: (error: unknown) => void | Promise<void>;
  onSettled?: () => void;
  context?: string;
};

type CallOptions = Parameters<
  ReturnType<typeof useVerifyLandlordIdentityExtendedWebApiHandler_verifyLandlordIdentityExtended>['mutate']
>[1] & {
  /** Per-call override for verifyErrors; falls back to onError when omitted. */
  onVerifyError?: (errors: Record<string, unknown>) => void | Promise<void>;
};

export function useVerifyLandlordIdentity(options?: Options) {
  const mutation = useVerifyLandlordIdentityExtendedWebApiHandler_verifyLandlordIdentityExtended({
    meta: {
      context: {
        api: 'VerifyLandlordIdentityExtendedWebApiHandler_verifyLandlordIdentityExtended',
        details: options?.context || 'useVerifyLandlordIdentity',
      },
    },
    onError: async (error) => {
      logError({ error, context: options?.context || 'useVerifyLandlordIdentity - onError' });
      await options?.onError?.(error);
    },
    onSettled: () => {
      options?.onSettled?.();
    },
  });

  const mutate = (variables: Parameters<typeof mutation.mutate>[0], callOptions?: CallOptions) => {
    mutation.mutate(variables, {
      ...callOptions,
      onSuccess: async (data, vars, ctx) => {
        if (data?.verifyErrors && Object.keys(data.verifyErrors).length > 0) {
          logError({
            error: new Error('verifyLandlordIdentityExtended returned errors'),
            context: options?.context || 'useVerifyLandlordIdentity - verifyErrors',
          });
          await (options?.onVerifyError ?? options?.onError)?.(data.verifyErrors);
          if (callOptions?.onVerifyError) {
            await callOptions.onVerifyError(data.verifyErrors);
          } else {
            await callOptions?.onError?.(data.verifyErrors as unknown as Error, vars, ctx);
          }
          return;
        }
        try {
          await options?.postVerificationFlow?.();
          await callOptions?.onSuccess?.(data, vars, ctx);
        } catch (error) {
          logError({ error, context: options?.context || 'useVerifyLandlordIdentity - postVerificationFlow' });
          await (options?.onPostVerificationError ?? options?.onError)?.(error);
          await callOptions?.onError?.(error as Error, vars, ctx);
        }
      },
    });
  };

  return { ...mutation, mutate };
}
